import React, { useEffect, useState } from "react";
import { FormikProps, withFormik } from "formik";
import { string, object, ref } from "yup";
import { Button, Input } from "components/shared";
import PasswordSvg from "assets/images/login/password.svg";
import { RootState } from "store";
import { useSelector } from "react-redux";
import { Eye, EyeOff } from "lucide-react";
export interface IResetProps {
  onSubmit: (values: any, actions: any) => void;
}

interface FormikValues {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

function ResetPassword(props: FormikProps<FormikValues> & IResetProps) {
  const { auth, resetFail } = useSelector((state: RootState) => state.auth);
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const visiblePassword = () => {
    setShowPassword(!showPassword);
  };
  const visibleNewPassword = () => {
    setShowNewPassword(!showNewPassword);
  };
  const visibleConfirmPassword = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };
  React.useEffect(() => {
    if (auth || resetFail) {
      props.resetForm();
      props.setSubmitting(false);
    }
  }, [auth, resetFail]);

  const isCurrentPassword = props.values.currentPassword;
  const isNewPassword = props.values.newPassword;
  const isConfirmPassword = props.values.confirmPassword;

  React.useEffect(() => {
    if (resetFail) {
      props.setFieldError("confirmPassword", resetFail);
    }
  }, [resetFail]);
  return (
    <form onSubmit={props.handleSubmit}>
      <div className=' flex max-w-5xl md:px-2 md:pb-1 items-center'>
        <div className='md:w-1/2'>
          <h2 className='font-bold flex justify-center sm:justify-start text-md md:text-xl text-theme700'>
            Need a Fresh Start?
          </h2>
          <p className='text-[11px] sm:text-xs mt-1 text-center sm:text-start text-theme600'>
            Provide your current password and set a new one.
          </p>

          <div className='grid grid-cols-1 mt-2 gap-3'>
            <div className='relative flex flex-col gap-2'>
              <Input
                label='Current Password'
                name='currentPassword'
                type={showPassword ? "text" : "password"}
                values={props.values}
                errors={props.errors}
                onChange={props.handleChange}
                onBlur={props.handleBlur}
                touches={props.touched}
                placeholder='Enter your current password'
                className='p-3 rounded-lg  border w-full text-xs'
              />

              {isCurrentPassword && (
                <div
                  className='absolute right-5 top-10 cursor-pointer'
                  onClick={(e) => {
                    e.stopPropagation();
                    visiblePassword();
                  }}
                >
                  {showPassword ? <Eye size={16} /> : <EyeOff size={16} />}
                </div>
              )}
            </div>

            <div className='relative flex flex-col gap-2'>
              <Input
                label='New Password'
                name='newPassword'
                type={showNewPassword ? "text" : "password"}
                values={props.values}
                errors={props.errors}
                onChange={props.handleChange}
                onBlur={props.handleBlur}
                touches={props.touched}
                placeholder='Enter your new password'
                className='p-3 rounded-lg border w-full text-xs'
              />

              {isNewPassword && (
                <div
                  className='absolute right-5 top-10 cursor-pointer'
                  onClick={(e) => {
                    e.stopPropagation();
                    visibleNewPassword();
                  }}
                >
                  {showNewPassword ? <Eye size={16} /> : <EyeOff size={16} />}
                </div>
              )}
            </div>

            <div className='relative flex flex-col gap-2'>
              <Input
                label='Confirm Password'
                name='confirmPassword'
                type={showConfirmPassword ? "text" : "password"}
                values={props.values}
                errors={props.errors}
                onChange={props.handleChange}
                onBlur={props.handleBlur}
                touches={props.touched}
                placeholder='Enter your confirm password'
                className='p-3 rounded-lg border w-full text-xs'
              />

              {isConfirmPassword && (
                <div
                  className='absolute right-5 top-10 cursor-pointer'
                  onClick={(e) => {
                    e.stopPropagation();
                    visibleConfirmPassword();
                  }}
                >
                  {showConfirmPassword ? (
                    <Eye size={16} />
                  ) : (
                    <EyeOff size={16} />
                  )}
                </div>
              )}
            </div>

            <Button
              name={props.isSubmitting ? "Changing" : "Change"}
              type='submit'
              disabled={props?.isSubmitting}
              className="mt-4"
            />
          </div>
        </div>

        <div className='md:block hidden w-1/2'>
          <img
            className='rounded-2xl'
            alt='login'
            src={PasswordSvg}
          />
        </div>
      </div>
    </form>
  );
}

export default withFormik<IResetProps, FormikValues>({
  validationSchema: object().shape({
    currentPassword: string().required("current password is required"),
    newPassword: string()
      .required("New Password is required")
      .min(8, "Password must be at least 8 characters."),
    confirmPassword: string()
      .required("Confirm Password is required")
      .oneOf([ref("newPassword"), ""], "Password does not match!"),
  }),

  mapPropsToValues: () => ({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  }),
  handleSubmit: (values, { props, ...actions }) => {
    props.onSubmit(values, actions);
  },
})(ResetPassword);
